import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import Footer from "../components/Footer";
import PageNav from "../components/PageNav";
import DownloadPdfButton from "../components/DownloadPdfButton";
import SearchBar from "../components/SearchBar";
import Sidebar from "../components/Sidebar";
import { getResourcePath, ROUTES } from "../../shared/constants/routes";
import {
  getResourceBySlug,
  getLocalizedPageDescription,
  getLocalizedPageKeywords,
  getLocalizedPageTitle,
  resourceNavigationPages,
} from "../resources/resourceRegistry";
import "../styles/sidebar.css";
import "../styles/mainContent.css";
import "../styles/resourcePages.css";

const ResourceDetailPage = () => {
  const { i18n, t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const isRtl = i18n.dir() === "rtl";
  const languageMenuRef = useRef(null);
  const mainWrapRef = useRef(null);

  const selectedPage = getResourceBySlug(slug);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setLanguageMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    mainWrapRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  const suggestions = useMemo(() => {
    if (!query.trim()) {
      return [];
    }

    const normalized = query.trim().toLowerCase();
    return resourceNavigationPages.flatMap((page) => {
      const title = getLocalizedPageTitle(page, t);
      const description = getLocalizedPageDescription(page, t);
      const translatedKeywords = getLocalizedPageKeywords(page, t);
      const keywords = [
        ...(page.keywords || []),
        ...(Array.isArray(translatedKeywords) ? translatedKeywords : []),
      ];
      const text = [title, description, ...keywords].join(" ").toLowerCase();

      if (!text.includes(normalized)) {
        return [];
      }

      return [{ slug: page.slug, title }];
    });
  }, [query, t]);

  if (!selectedPage) {
    return <Navigate to={ROUTES.QUICK_START} replace />;
  }

  const selectedIndex = resourceNavigationPages.findIndex(
    (page) => page.slug === selectedPage.slug,
  );
  const prevPage =
    selectedIndex > 0 ? resourceNavigationPages[selectedIndex - 1] : null;
  const nextPage =
    selectedIndex >= 0 && selectedIndex < resourceNavigationPages.length - 1
      ? resourceNavigationPages[selectedIndex + 1]
      : null;

  const ContentComponent = selectedPage.component;

  const handleSelect = (targetSlug) => {
    setQuery("");
    navigate(getResourcePath(targetSlug));
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const currentLanguage =
    (i18n.resolvedLanguage || i18n.language) === "ar" ? "ar" : "en";

  const languageOptions = [
    { code: "en", label: "English", shortLabel: "en" },
    { code: "ar", label: "العربية", shortLabel: "ع" },
  ];

  const selectedLanguageOption =
    languageOptions.find((option) => option.code === currentLanguage) ||
    languageOptions[0];

  const getLanguageOptionLabel = (option) =>
    isMobile ? option.shortLabel : option.label;

  const handleLanguageSelect = (langCode) => {
    setLanguageMenuOpen(false);
    if (langCode !== currentLanguage) {
      i18n.changeLanguage(langCode);
    }
  };

  return (
    <SimpleBar style={{ maxHeight: "100vh", height: "100vh" }} autoHide={false}>
      <div className="resource-layout-shell" dir={i18n.dir()}>
        <div
          className={`sidebar-container${isMobile ? " mobile" : ""}${
            sidebarOpen ? " open" : ""
          }`}
          style={
            isMobile
              ? {
                  position: "fixed",
                  left: isRtl ? "auto" : sidebarOpen ? 0 : "-100%",
                  right: isRtl ? (sidebarOpen ? 0 : "-100%") : "auto",
                  top: 0,
                  bottom: 0,
                  zIndex: 300,
                  width: 320,
                  transition:
                    "left 0.3s cubic-bezier(.4,0,.2,1), right 0.3s cubic-bezier(.4,0,.2,1)",
                  boxShadow: sidebarOpen
                    ? isRtl
                      ? "-2px 0 16px rgba(0,0,0,0.18)"
                      : "2px 0 16px rgba(0,0,0,0.18)"
                    : "none",
                  background: "transparent",
                }
              : {
                  position: "fixed",
                  left: isRtl ? "auto" : 0,
                  right: isRtl ? 0 : "auto",
                  top: 0,
                  bottom: 0,
                  zIndex: 100,
                  width: 400,
                }
          }
        >
          {isMobile && (
            <button
              className="close-btn"
              type="button"
              style={{
                position: "absolute",
                top: 18,
                right: isRtl ? "auto" : 18,
                left: isRtl ? 18 : "auto",
                zIndex: 400,
                background: "none",
                border: "none",
                fontSize: 32,
                color: "#3b581d",
                cursor: "pointer",
              }}
              onClick={() => setSidebarOpen(false)}
              aria-label={t("sidebar.close")}
            >
              x
            </button>
          )}

          <Sidebar
            items={resourceNavigationPages}
            selectedSlug={selectedPage.slug}
            onSelect={handleSelect}
          />
        </div>

        {isMobile && sidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className="resource-main-wrap" ref={mainWrapRef}>
          <div className="main-content resource-detail-main">
            <div className="resource-topbar">
              {isMobile && !sidebarOpen && (
                <button
                  className="hamburger-btn"
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  aria-label={t("sidebar.open")}
                >
                  <span className="hamburger-line line-one" />
                  <span className="hamburger-line line-two" />
                  <span className="hamburger-line line-three" />
                </button>
              )}

              <div className="resource-search-wrap">
                <div className="resource-search-controls">
                  <SearchBar
                    onSearch={setQuery}
                    query={query}
                    suggestions={suggestions}
                    onSuggestionSelect={handleSelect}
                  />

                  <DownloadPdfButton />

                  <div className="lang-dropdown" ref={languageMenuRef}>
                    <button
                      type="button"
                      className={`lang-dropdown-toggle${languageMenuOpen ? " open" : ""}`}
                      onClick={() => setLanguageMenuOpen((prev) => !prev)}
                      aria-label={t("language.toggleLabel")}
                      aria-haspopup="menu"
                      aria-expanded={languageMenuOpen}
                    >
                      <span className="lang-toggle-main">
                        <span className="lang-toggle-globe" aria-hidden="true">
                          <svg viewBox="0 0 24 24" focusable="false">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M2 12h20" />
                            <path d="M12 2c3.2 3 3.2 17 0 20" />
                            <path d="M12 2c-3.2 3-3.2 17 0 20" />
                            <path d="M5 7.5h14" />
                            <path d="M5 16.5h14" />
                          </svg>
                        </span>
                        <span className="lang-current-label">
                          {getLanguageOptionLabel(selectedLanguageOption)}
                        </span>
                      </span>

                      <span
                        className="lang-dropdown-caret"
                        aria-hidden="true"
                      />
                    </button>

                    {languageMenuOpen && (
                      <div className="lang-dropdown-menu" role="menu">
                        {languageOptions.map((option) => (
                          <button
                            key={option.code}
                            type="button"
                            className={`lang-menu-item${
                              option.code === currentLanguage ? " selected" : ""
                            }`}
                            role="menuitemradio"
                            aria-checked={option.code === currentLanguage}
                            onClick={() => handleLanguageSelect(option.code)}
                            title={option.label}
                            aria-label={option.label}
                          >
                            <span className="lang-item-label">
                              {getLanguageOptionLabel(option)}
                            </span>
                            {option.code === currentLanguage && (
                              <span
                                className="lang-item-check"
                                aria-hidden="true"
                              >
                                ✓
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <ContentComponent page={selectedPage} />

            <PageNav
              prevPage={prevPage}
              nextPage={nextPage}
              onSelect={handleSelect}
            />

            <Footer />
          </div>
        </div>
      </div>
    </SimpleBar>
  );
};

export default ResourceDetailPage;
